/* UV Cart Tier Progress Bar v1.4 — Explicit complete tier controls and 5-to-10 stage, quantity-based, Horizon 4.2.0 */
(() => {
  'use strict';

  const GLOBAL_KEY = '__uvCartTierProgressBarManagerV1';
  const CART_LINES_EVENT = 'shopify:cart:lines-update';
  const CART_DISCOUNT_EVENT = 'shopify:cart:discount-update';
  const CART_VIEW_EVENT = 'shopify:cart:view';
  const DRAWER_SELECTOR = '#cart-drawer';
  const HEADER_SELECTOR = '.theme-drawer__header';

  if (window[GLOBAL_KEY]?.destroy) window[GLOBAL_KEY].destroy();

  const instances = new Map();
  const optionSections = new Map();
  let cartRevision = 0;
  let drawerObserver = null;
  let observerFrame = 0;
  let fallbackTimer = 0;
  let destroyed = false;
  let lastNativeEventAt = 0;

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  const parseConfig = (node) => {
    try {
      return JSON.parse(node.textContent || '{}');
    } catch (error) {
      console.warn('[UV Cart Tier Bar] Invalid config JSON', error);
      return null;
    }
  };

  const getDrawer = () => document.querySelector(DRAWER_SELECTOR);

  const getPrimaryInstance = () => {
    for (const instance of instances.values()) {
      if (instance.config.enabled) return instance;
    }
    return null;
  };

  // Cart quantities count units, including repeated variants and mixed products.
  const normalizeQuantity = (value) => {
    if (value == null || value === '') return null;
    const count = Number(value);
    return Number.isSafeInteger(count) && count >= 0 ? count : null;
  };

  const sumQuantities = (items) => {
    if (!Array.isArray(items)) return null;
    let total = 0;
    for (const item of items) {
      const quantity = normalizeQuantity(item?.quantity);
      if (quantity == null) return null;
      total += quantity;
    }
    return normalizeQuantity(total);
  };

  const normalizeEventResult = (result) => {
    if (!result || typeof result !== 'object') return null;
    const itemCount = normalizeQuantity(result.cart?.totalQuantity)
      ?? normalizeQuantity(result.detail?.itemCount)
      ?? sumQuantities(result.detail?.items);
    return itemCount == null ? null : { itemCount };
  };

  const resolveText = (template, tier, itemToken = '') => String(template || '')
    .replace(/\[reward\]/g, tier.reward || '')
    .replace(/\[item\]/g, itemToken || '')
    .replace(/\s+/g, ' ')
    .trim();

  const setHeadingText = (instance) => {
    const drawer = getDrawer();
    if (!drawer) return;
    const heading = drawer.querySelector('#cart-drawer-heading.theme-drawer__title');
    if (!heading || !heading.querySelector('.theme-drawer__badge')) return;

    const desired = String(instance.config.headingText || '').trim();
    if (!desired) return;

    const badge = heading.querySelector('.theme-drawer__badge');
    let textNode = Array.from(heading.childNodes).find((node) => node.nodeType === Node.TEXT_NODE);
    if (!textNode) {
      textNode = document.createTextNode(`${desired} `);
      heading.insertBefore(textNode, badge || heading.firstChild);
    } else if (textNode.textContent.trim() !== desired) {
      textNode.textContent = `${desired} `;
    }
  };

  const ensureBar = (instance) => {
    if (!instance.config.enabled) return null;
    const drawer = getDrawer();
    const header = drawer?.querySelector(HEADER_SELECTOR);
    if (!drawer || !header) return null;

    let bar = drawer.querySelector(`[data-uv-ctp-bar][data-uv-ctp-owner="${CSS.escape(instance.config.sectionId)}"]`);
    if (!bar) {
      const template = document.getElementById(instance.config.templateId);
      if (!(template instanceof HTMLTemplateElement)) return null;
      const clone = template.content.firstElementChild?.cloneNode(true);
      if (!(clone instanceof HTMLElement)) return null;
      header.insertAdjacentElement('afterend', clone);
      bar = clone;
      bar.style.setProperty('--uv-ctp-icon-size', `${Number(instance.config.iconSize || 20)}px`);
    } else if (bar.previousElementSibling !== header) {
      header.insertAdjacentElement('afterend', bar);
    }

    instance.bar = bar;
    setHeadingText(instance);
    return bar;
  };

  // Options live in a block in the companion style section. No edits to the
  // existing main section or its saved reward copy/icons are needed.
  const getOptions = () => Array.from(optionSections.values()).find((options) => options.enabled) || null;

  const isTierEnabled = (options, slot, optional = false) => {
    if (!options) return !optional;
    const explicit = options[`enableTier${slot}`];
    if (typeof explicit === 'boolean') return explicit;
    // Preserve saved switches and support the older companion Liquid config.
    return optional ? options[`show${slot}`] === true : options[`show${slot}`] !== false;
  };

  const getTierModel = (instance, snapshot) => {
    const options = getOptions();
    const legacy = instance.config.tiers.map((tier, index) => ({
      ...tier, key: `legacy-${index}`, threshold: Number(tier.threshold),
      hero: index === instance.config.tiers.length - 1,
      enabled: isTierEnabled(options, [2, 3, 5][index] ?? Number(tier.threshold)),
      position: options?.[`position${[2, 3, 5][index] ?? Number(tier.threshold)}`],
    })).filter((tier) => Number.isFinite(tier.threshold) && tier.threshold > 0);
    const extra = (threshold, hero) => ({
      key: `extra-${threshold}`, threshold, hero,
      position: options?.[`position${threshold}`],
      reward: options?.[`reward${threshold}`] || 'Discount',
      label: options?.[`label${threshold}`] || String(threshold),
      goal: options?.[`goal${threshold}`] || '[item] to unlock [reward]',
      success: options?.[`success${threshold}`] || '[reward] unlocked!',
    });
    const stageTen = Boolean(isTierEnabled(options, 10, true) && snapshot.itemCount >= 5);
    let tiers = stageTen ? [extra(10, true)] : legacy.filter((tier) => tier.enabled);
    if (!stageTen && isTierEnabled(options, 4, true) && !tiers.some((tier) => tier.threshold === 4)) tiers.push(extra(4, false));
    tiers.sort((left, right) => left.threshold - right.threshold);
    const finalThreshold = tiers.at(-1)?.threshold || 5;
    tiers = tiers.map((tier) => ({
      ...tier,
      position: tier.position == null ? clamp(tier.threshold / finalThreshold * 100
        - (tier.hero ? 0 : Number(instance.config.checkpointShift || 0)), 0, 100)
        : clamp(Number(tier.position) || 0, 0, 100),
    }));
    return { tiers, stageTen, base: stageTen ? 5 : 0, finalThreshold, options };
  };

  const ensureCheckpoints = (bar, model) => {
    const originals = Array.from(bar.querySelectorAll('[data-uv-ctp-checkpoint]'));
    originals.forEach((checkpoint, index) => {
      if (!checkpoint.dataset.uvCtpTierKey) checkpoint.dataset.uvCtpTierKey = `legacy-${index}`;
    });
    for (const tier of model.tiers) {
      if (bar.querySelector(`[data-uv-ctp-tier-key="${tier.key}"]`)) continue;
      if (!tier.key.startsWith('extra-')) continue;
      const host = originals[0]?.parentElement || bar.querySelector('.uv-ctp__track-wrap');
      if (!host) continue;
      const checkpoint = document.createElement('span');
      checkpoint.className = `uv-ctp__checkpoint${tier.hero ? ' uv-ctp__checkpoint--hero' : ''}`;
      checkpoint.setAttribute('data-uv-ctp-checkpoint', '');
      checkpoint.dataset.uvCtpTierKey = tier.key;
      // Static internal SVG; all merchant-provided copy is set with textContent.
      checkpoint.innerHTML = '<span class="uv-ctp__icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M3 3h8l10 10-8 8L3 11z"/><circle cx="7.5" cy="7.5" r="1"/></svg></span><span class="uv-ctp__amount"></span><span class="uv-ctp__check" aria-hidden="true">✓</span>';
      host.append(checkpoint);
    }
    const byKey = new Map();
    bar.querySelectorAll('[data-uv-ctp-checkpoint]').forEach((checkpoint) => {
      byKey.set(checkpoint.dataset.uvCtpTierKey, checkpoint);
      const tier = model.tiers.find((candidate) => candidate.key === checkpoint.dataset.uvCtpTierKey);
      const hidden = !tier;
      if (checkpoint.hidden !== hidden) checkpoint.hidden = hidden;
      if (tier) {
        if (tier.key.startsWith('extra-')) {
          const amount = checkpoint.querySelector('.uv-ctp__amount');
          const pillText = resolveText(tier.label, tier);
          if (amount && amount.textContent !== pillText) amount.textContent = pillText;
        }
        const label = `${tier.threshold} ${tier.threshold === 1 ? 'item' : 'items'}: ${tier.reward || 'reward'}`;
        if (checkpoint.getAttribute('aria-label') !== label) checkpoint.setAttribute('aria-label', label);
        // The clamp prevents pills from hanging outside the bar at 0% or 100%.
        if (!checkpoint.hasAttribute('data-uv-ctp-positioned')) checkpoint.setAttribute('data-uv-ctp-positioned', '');
        checkpoint.style.setProperty('--uv-ctp-position', `${tier.position}%`);
        checkpoint.style.setProperty('--uv-ctp-pill-half-width', `${checkpoint.offsetWidth / 2}px`);
      }
    });
    return byKey;
  };

  const writeMessage = (instance, bar, snapshot, model, temporarySuccessTier = null) => {
    const messageEl = bar.querySelector('[data-uv-ctp-message]');
    if (!messageEl || !model.tiers.length) return;
    const tier = model.tiers.find((candidate) => snapshot.itemCount < candidate.threshold);
    if (!tier || temporarySuccessTier) {
      const successTier = temporarySuccessTier || model.tiers.at(-1);
      const span = document.createElement('span');
      span.className = tier ? 'uv-ctp__success' : 'uv-ctp__done';
      span.textContent = resolveText(successTier.success, successTier);
      messageEl.replaceChildren(span);
      return;
    }
    const remaining = Math.max(0, tier.threshold - snapshot.itemCount);
    const accent = document.createElement('span');
    accent.className = `uv-ctp__accent${instance.config.remainingPill ? ' uv-ctp__accent--pill' : ''}`;
    accent.textContent = `${remaining} more`;
    const itemToken = remaining === 1 ? instance.config.itemSingular : instance.config.itemPlural;
    const suffix = resolveText(tier.goal, tier, itemToken);
    const nodes = [document.createTextNode('Add '), accent];
    if (suffix) nodes.push(document.createTextNode(` ${suffix}`));
    messageEl.replaceChildren(...nodes);
  };

  const animateCheckpoint = (instance, checkpoint) => {
    if (!instance.config.enablePop || !(checkpoint instanceof HTMLElement)) return;
    checkpoint.classList.remove('uv-ctp-pop');
    void checkpoint.offsetWidth;
    checkpoint.classList.add('uv-ctp-pop');
    window.setTimeout(() => checkpoint.classList.remove('uv-ctp-pop'), 340);
  };

  const getFill = (instance, snapshot, model) => {
    if (snapshot.itemCount >= model.finalThreshold) return 100;
    if (model.stageTen) return clamp((snapshot.itemCount - 5) / 5 * 100, 0, 100);
    // Preserve original quantity-based fill until the optional controls are used.
    let fill = clamp(snapshot.itemCount / model.finalThreshold * 100, 0, 100);
    if (model.options) {
      // Interpolate through enabled checkpoints so progress meets moved pills.
      let before = { threshold: 0, position: 0 };
      for (const tier of model.tiers) {
        const after = { ...tier, position: Math.max(before.position, tier.position) };
        if (snapshot.itemCount <= tier.threshold) {
          const ratio = (snapshot.itemCount - before.threshold) / (tier.threshold - before.threshold);
          fill = before.position + ratio * (after.position - before.position);
          break;
        }
        before = after;
      }
    }
    const reachedIndex = model.tiers.findLastIndex((tier) => snapshot.itemCount >= tier.threshold);
    if (reachedIndex >= 0) {
      const tier = model.tiers[reachedIndex];
      const nextPosition = model.tiers[reachedIndex + 1]?.position ?? 100;
      const limit = Math.min(99, Math.max(tier.position, nextPosition - 1));
      fill = Math.max(fill, Math.min(limit, tier.position + Number(instance.config.progressNudge || 0)));
    }
    return clamp(fill, 0, 100);
  };

  const applySnapshot = (instance, snapshot, { initial = false } = {}) => {
    if (!snapshot || destroyed) return;
    instance.lastSnapshot = snapshot;
    if (instance.successTimer) clearTimeout(instance.successTimer);
    instance.successTimer = 0;
    const bar = ensureBar(instance);
    if (!bar) return;
    const model = getTierModel(instance, snapshot);
    const previousCount = instance.previousCount ?? snapshot.itemCount;
    const previousStage = instance.stageTen;
    instance.previousCount = snapshot.itemCount;
    instance.stageTen = model.stageTen;
    bar.hidden = !model.tiers.length || Boolean(instance.config.hideWhenEmpty && snapshot.itemCount <= 0);
    if (bar.hidden) return;
    bar.dataset.uvCtpStage = model.stageTen ? 'ten' : 'standard';
    // Stage changes reset immediately, without animating backwards through old progress.
    if (previousStage !== undefined && previousStage !== model.stageTen) {
      bar.classList.add('uv-ctp-stage-reset');
    }
    const fill = getFill(instance, snapshot, model);
    bar.style.setProperty('--uv-ctp-progress', `${fill}%`);
    const complete = snapshot.itemCount >= model.finalThreshold;
    const anyReached = model.tiers.some((tier) => snapshot.itemCount >= tier.threshold);
    bar.style.setProperty('--uv-ctp-fill', complete ? 'var(--uv-ctp-gold)' : anyReached ? 'var(--uv-ctp-success)' : 'var(--uv-ctp-fill-base)');
    bar.style.setProperty('--uv-ctp-icon-size', `${Number(instance.config.iconSize || 20)}px`);
    const progress = bar.querySelector('[data-uv-ctp-progressbar]');
    progress?.setAttribute('aria-valuenow', String(Math.round(clamp((snapshot.itemCount - model.base) / (model.finalThreshold - model.base) * 100, 0, 100))));
    progress?.setAttribute('aria-valuetext', `${snapshot.itemCount} items in cart; ${model.stageTen ? 'second stage, from 5 to 10 items' : `final reward at ${model.finalThreshold} items`}`);
    const checkpoints = ensureCheckpoints(bar, model);
    const nextTier = model.tiers.find((tier) => snapshot.itemCount < tier.threshold);
    let newlyReached = null;
    model.tiers.forEach((tier) => {
      const checkpoint = checkpoints.get(tier.key);
      const reached = snapshot.itemCount >= tier.threshold;
      checkpoint?.classList.toggle('is-reached', reached);
      checkpoint?.classList.toggle('is-next', nextTier?.key === tier.key && !tier.hero);
      if (!initial && reached && previousCount < tier.threshold) newlyReached = tier;
    });
    if (newlyReached) animateCheckpoint(instance, checkpoints.get(newlyReached.key));
    // Show the 10-item goal immediately on the 5-item transition.
    const temporarySuccessTier = newlyReached && !complete && previousStage === model.stageTen ? newlyReached : null;
    writeMessage(instance, bar, snapshot, model, temporarySuccessTier);
    if (temporarySuccessTier && Number(instance.config.successDuration) > 0) {
      instance.successTimer = window.setTimeout(() => {
        instance.successTimer = 0;
        const liveBar = ensureBar(instance);
        if (liveBar && instance.lastSnapshot) writeMessage(instance, liveBar, instance.lastSnapshot, getTierModel(instance, instance.lastSnapshot));
      }, Number(instance.config.successDuration));
    } else if (temporarySuccessTier) {
      writeMessage(instance, bar, snapshot, model);
    }
    if (bar.classList.contains('uv-ctp-stage-reset')) {
      // Commit the new width while transitions are disabled, then restore them.
      void bar.offsetWidth;
      bar.classList.remove('uv-ctp-stage-reset');
    }
  };

  const fetchFallbackCart = async () => {
    const root = window.Shopify?.routes?.root || '/';
    const url = `${root.endsWith('/') ? root : `${root}/`}cart.js`;
    const response = await fetch(url, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store',
    });
    if (!response.ok) throw new Error(`Cart fallback failed: ${response.status}`);
    return response.json();
  };

  const normalizeAjaxCart = (cart) => {
    if (!cart || typeof cart !== 'object') return null;
    const itemCount = normalizeQuantity(cart.item_count) ?? sumQuantities(cart.items);
    return itemCount == null ? null : { itemCount };
  };

  const getNativeDrawerCount = () => {
    const badge = getDrawer()?.querySelector('#cart-drawer-heading .theme-drawer__badge');
    if (!badge) return null;
    const value = Number.parseInt(badge.textContent || '', 10);
    return Number.isFinite(value) ? value : null;
  };

  const scheduleFallback = ({ force = false } = {}) => {
    clearTimeout(fallbackTimer);
    fallbackTimer = window.setTimeout(async () => {
      if (destroyed) return;
      const instance = getPrimaryInstance();
      const drawer = getDrawer();
      if (!instance || !drawer?.hasAttribute('open')) return;

      if (!force) {
        // Native Horizon events are the source of truth. Only repair when the
        // visible native cart count proves that a non-standard mutation happened.
        if (Date.now() - lastNativeEventAt < 900) return;
        const nativeCount = getNativeDrawerCount();
        if (nativeCount == null || nativeCount === Number(instance.lastSnapshot?.itemCount)) return;
      }

      try {
        const revision = cartRevision;
        const cart = await fetchFallbackCart();
        if (!destroyed && revision === cartRevision) applySnapshot(instance, normalizeAjaxCart(cart));
      } catch (error) {
        if (error?.name !== 'AbortError') console.warn('[UV Cart Tier Bar] fallback sync failed', error);
      }
    }, force ? 180 : 350);
  };

  const handleResolvedResult = (result) => {
    const instance = getPrimaryInstance();
    if (!instance) return;
    if (result?.detail?.didError) return;

    const snapshot = normalizeEventResult(result);
    if (snapshot) applySnapshot(instance, snapshot);
    else scheduleFallback({ force: true });
  };

  const onCartLinesUpdate = (event) => {
    lastNativeEventAt = Date.now();
    const revision = ++cartRevision;
    const instance = getPrimaryInstance();
    if (instance) ensureBar(instance);
    const promise = event?.promise;
    if (promise?.then) {
      promise.then((result) => {
        if (!destroyed && revision === cartRevision) handleResolvedResult(result);
      }).catch((error) => {
        if (!destroyed && revision === cartRevision && error?.name !== 'AbortError') scheduleFallback({ force: true });
      });
    } else {
      scheduleFallback({ force: true });
    }
  };

  const onCartDiscountUpdate = () => {
    // Applying/removing a discount does not change cart quantity.
    const instance = getPrimaryInstance();
    if (instance) ensureBar(instance);
  };

  const onCartView = () => {
    const instance = getPrimaryInstance();
    if (!instance) return;
    ensureBar(instance);
    if (instance.lastSnapshot) applySnapshot(instance, instance.lastSnapshot, { initial: true });
    scheduleFallback({ force: true });
  };

  const observeDrawer = () => {
    drawerObserver?.disconnect();
    const drawer = getDrawer();
    if (!drawer) return;
    drawerObserver = new MutationObserver(() => {
      if (observerFrame) return;
      observerFrame = requestAnimationFrame(() => {
        observerFrame = 0;
        const instance = getPrimaryInstance();
        if (!instance) return;
        const before = instance.bar;
        const bar = ensureBar(instance);
        if (bar && bar !== before && instance.lastSnapshot) applySnapshot(instance, instance.lastSnapshot, { initial: true });
        setHeadingText(instance);
      });
      scheduleFallback();
    });
    drawerObserver.observe(drawer, { childList: true, subtree: true });
  };

  const registerConfigNode = (node) => {
    const config = parseConfig(node);
    if (!config?.sectionId) return;

    const previous = instances.get(config.sectionId);
    if (previous?.successTimer) clearTimeout(previous.successTimer);
    // Rebuild from the current Liquid template when main-section labels/icons change.
    previous?.bar?.remove();

    const instance = {
      config: {
        ...config,
        iconSize: Number(config.iconSize || 20),
      },
      bar: null,
      lastSnapshot: previous?.lastSnapshot || config.initial,
      previousCount: previous?.previousCount ?? Number(config.initial?.itemCount || 0),
      stageTen: previous?.stageTen,
      successTimer: 0,
    };

    instances.set(config.sectionId, instance);
  };

  const scanConfigs = (root = document) => {
    root.querySelectorAll?.('[data-uv-ctp-options]').forEach((node) => {
      const options = parseConfig(node);
      if (options?.sectionId) optionSections.set(options.sectionId, options);
    });
    root.querySelectorAll?.('[data-uv-ctp-config]').forEach(registerConfigNode);
    const instance = getPrimaryInstance();

    for (const candidate of instances.values()) {
      if (candidate !== instance) {
        candidate.bar?.remove();
        candidate.bar = null;
      }
    }

    if (instance) {
      ensureBar(instance);
      applySnapshot(instance, instance.lastSnapshot || instance.config.initial, { initial: true });
      setHeadingText(instance);
    }
    observeDrawer();
  };

  const onSectionLoad = (event) => {
    const sectionId = event.detail?.sectionId || event.target?.dataset?.sectionId;
    // A block can be deleted while its section survives, removing its config node.
    if (sectionId) optionSections.delete(sectionId);
    scanConfigs(event.target || document);
  };
  const onSectionUnload = (event) => {
    const sectionId = event.detail?.sectionId || event.target?.dataset?.sectionId;
    if (sectionId) optionSections.delete(sectionId);
    if (sectionId && instances.has(sectionId)) {
      const instance = instances.get(sectionId);
      if (instance?.successTimer) clearTimeout(instance.successTimer);
      instance?.bar?.remove();
      instances.delete(sectionId);
    }
    const current = getPrimaryInstance();
    if (current) applySnapshot(current, current.lastSnapshot, { initial: true });
  };

  const initialize = () => {
    scanConfigs(document);
    document.addEventListener(CART_LINES_EVENT, onCartLinesUpdate);
    document.addEventListener(CART_DISCOUNT_EVENT, onCartDiscountUpdate);
    document.addEventListener(CART_VIEW_EVENT, onCartView);
    document.addEventListener('shopify:section:load', onSectionLoad);
    document.addEventListener('shopify:section:unload', onSectionUnload);
  };

  const destroy = () => {
    destroyed = true;
    clearTimeout(fallbackTimer);
    if (observerFrame) cancelAnimationFrame(observerFrame);
    drawerObserver?.disconnect();
    document.removeEventListener(CART_LINES_EVENT, onCartLinesUpdate);
    document.removeEventListener(CART_DISCOUNT_EVENT, onCartDiscountUpdate);
    document.removeEventListener(CART_VIEW_EVENT, onCartView);
    document.removeEventListener('shopify:section:load', onSectionLoad);
    document.removeEventListener('shopify:section:unload', onSectionUnload);
    for (const instance of instances.values()) {
      if (instance.successTimer) clearTimeout(instance.successTimer);
      instance.bar?.remove();
    }
    instances.clear();
    optionSections.clear();
  };

  window[GLOBAL_KEY] = { destroy, scanConfigs };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
